import React, { FC, useCallback, useEffect, useState } from 'react';
import { Table, TablePaginationConfig, Tag, Typography, Skeleton, Space, Tooltip, Button, Popconfirm } from 'antd';

import { type IRecommendation, IServiceItem } from 'types/recommendations.types';
import { LoadListDataParams } from 'hooks/useListData';
import { useAppSelector } from 'store';
import { citiesSelector } from 'store/dictionaries/dictionaries.selectors';
import { getOptionLabel } from 'utils/format';
import { formatWorkTimeInterval } from 'utils/date';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

interface Props<T = any> {
  items: T[];
  pagination: TablePaginationConfig;
  loading: boolean;
  onTableChange: (pagination: TablePaginationConfig, newFilters: Partial<LoadListDataParams>) => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
}

export const RecommendationsList: FC<Props> = ({ items, pagination, loading, onTableChange, onEdit, onDelete }) => {
  const { options: cities, loading: citiesLoading } = useAppSelector(citiesSelector);

  return (
    <div>
      <Table
        rowKey={'id'}
        loading={loading}
        dataSource={items}
        pagination={pagination}
        onChange={onTableChange}
        columns={[
          {
            dataIndex: 'items',
            title: 'Услуги',
            align: 'center',
            render: (items: IServiceItem[]) => (
              <div>
                {items.map((item) => (
                  <Tag style={{ maxWidth: '100%', margin: '4px' }} key={item.id + '/' + item.city.id}>
                    <Typography.Text className="ant-tag1">
                      {item.name} ({item.city.name})
                    </Typography.Text>
                  </Tag>
                ))}
              </div>
            ),
          },
          {
            dataIndex: 'cityIds',
            title: 'Города',
            align: 'center',
            render: (cityIds: IRecommendation['cityIds']) => {
              if (citiesLoading) return <Skeleton.Button />;
              const cityNames = (cityIds || []).map((cityId) => getOptionLabel(cities, cityId));
              return cityIds ? cityNames.join(', ') : '';
            },
          },
          {
            dataIndex: 'workTimeInterval',
            title: 'Период действия',
            align: 'center',
            render: (val: IRecommendation['workTimeInterval']) => formatWorkTimeInterval(val),
            width: 200,
          },
          {
            title: 'Действия',
            align: 'center',
            render: (_, data) => (
              <Space>
                <Tooltip title="Редактировать">
                  <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(data)} />
                </Tooltip>
                <Popconfirm
                  placement="topRight"
                  title="Подтвердите действие"
                  onConfirm={() => onDelete(data)}
                  zIndex={2}
                  okText={''}
                  cancelText={''}
                  okButtonProps={{
                    icon: <CheckOutlined />,
                    size: 'small',
                    danger: true,
                  }}
                  cancelButtonProps={{
                    icon: <CloseOutlined />,
                    size: 'small',
                  }}
                >
                  <Tooltip title="Удалить" zIndex={1}>
                    <Button type="text" icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
};
